<template>
  <ModalBase :show-modal="showModal" @close-modal="close" :width-md="view !== 'close-editing-view'" :narrow="view === 'close-editing-view'" auto-h class="send-invoice-adjust-modal">
    <template v-slot:titlebar>
      <TitleBar variant-class="title-modal title-modal--simple">
        <template v-slot:title>
          <span v-if="view === 'close-editing-view'">{{ $gettext('Mark as sent') }}</span>
          <span v-if="view === 'numbering-view'">{{ $gettext('This invoice number is not in sequence') }}</span>
          <span v-if="view === 'date-view'">{{ $gettext('The invoice date is not set to today') }}</span>
        </template>
      </TitleBar>
    </template>
    <template v-slot:maincontent>
      <div class="adjust-invoice">

        <div v-if="view === 'numbering-view'" key="numbering-view" class="adjust-invoice__numbering">
          <div class="adjust-invoice__body-header">
            <p v-translate>There are other Draft invoices in your account with lower numbers. If you send these invoices at a later date or delete them later, you could be creating gaps in your invoice numbering.</p>
          </div>
          <div class="adjust-invoice__number-selector">
            <div class="adjust-invoice__selector-title" v-translate>
              Select an invoice number
            </div>
            <div class="adjust-invoice__options">
              <div
                @click="setSequenced(false)"
                class="adjust-invoice__option adjust-invoice__option--first"
                :class="{ 'adjust-invoice__option--active': !isSequenced }"
              >
                <span class="sub-text">{{ $gettext('Current number') }}</span>
                <span class="main-text">{{ invoices[1].number }}</span>
              </div>

              <div class="adjust-selection-arrow adjust-selection-arrow-left" :class="{ 'adjust-selection-arrow--active': !isSequenced }">
                <font-awesome-icon icon="fa-solid fa-arrow-left" class="mr-1 ml-2" />
              </div>
              <div  class="adjust-selection-arrow adjust-selection-arrow-right" :class="{ 'adjust-selection-arrow--active': isSequenced }">
                <font-awesome-icon icon="fa-solid fa-arrow-right" class="mr-2" />
              </div>

              <div 
                @click="setSequenced(true)"
                class="adjust-invoice__option adjust-invoice__option--second"
                :class="{ 'adjust-invoice__option--active': isSequenced }"
              >
                <span class="sub-text">{{ $gettext('Suggested number') }}</span>
                <span class="main-text">{{ next_number }}</span>
              </div>
            </div>
          </div>

          <ul class="adjust-invoice__item-list gk-table"> 
            <!-- CHOICE -->
            <li 
              class="adjust-invoice__row-item adjust-invoice__row-item--choice"
              :class="{ 'adjust-invoice__row-item--sequenced': isSequenced, 'adjust-invoice__row-item--not-sequenced': !isSequenced }"
            >
              <div class="adjust-invoice__item-data adjust-invoice__item-data--darker">
                <span v-if="!isSequenced">
                  {{ invoices[1].number }}
                </span>
                <span v-if="isSequenced">
                  {{ invoices[2].number }}
                </span>
              </div>
              <div class="adjust-invoice__item-data adjust-invoice__item-data--fixed-md">
                <span>
                  {{ invoices[1].date | formatDate }}
                </span>
              </div>
              <div class="adjust-invoice__item-data adjust-invoice__item-data--fixed-md">
                <span>
                  {{ invoices[1].amount | currency(invoices[1].currency) }}
                </span>
              </div>
              <div class="adjust-invoice__item-data adjust-invoice__item-data--badge">
                <span v-translate class="status-badge status-sending">
                  Sending
                </span>
              </div>
            </li>
            <!-- DRAFT -->
            <li class="adjust-invoice__row-item adjust-invoice__row-item--draft">
              <div class="adjust-invoice__item-data">
                <span v-if="!isSequenced">
                  {{ invoices[2].number }}
                </span>
                <span v-if="isSequenced">
                  {{ invoices[1].number }}
                </span>
              </div>
              <div class="adjust-invoice__item-data adjust-invoice__item-data--fixed-md">
                <span>
                  {{ invoices[2].date | formatDate }}
                </span>
              </div>
              <div class="adjust-invoice__item-data adjust-invoice__item-data--fixed-md">
                <span>
                  {{ invoices[2].amount | currency(invoices[2].currency) }}
                </span>
              </div>
              <div class="adjust-invoice__item-data adjust-invoice__item-data--badge">
                <span v-translate class="status-badge status-unassigned">
                  Draft
                </span>
              </div>
            </li>
            <!-- DOTS -->
            <li class="adjust-invoice__row-item adjust-invoice__row-item--transparent">
              <span class="adjust-invoice__row-ellipsis">
                <font-awesome-icon icon="fa-solid fa-circle" />
                <font-awesome-icon icon="fa-solid fa-circle" />
                <font-awesome-icon icon="fa-solid fa-circle" />
              </span>
            </li>
            <!-- REFERENCE -->
            <li class="adjust-invoice__row-item adjust-invoice__row-item--reference">
              <div class="adjust-invoice__item-data">
                <span>
                  {{ invoices[0].number }}
                </span>
              </div>
              <div class="adjust-invoice__item-data adjust-invoice__item-data--fixed-md">
                <span>
                  {{ invoices[0].date | formatDate }}
                </span>
              </div>
              <div class="adjust-invoice__item-data adjust-invoice__item-data--fixed-md">
                <span>
                  {{ invoices[0].amount | currency(invoices[0].currency) }}
                </span>
              </div>
              <div class="adjust-invoice__item-data adjust-invoice__item-data--badge">
                <span class="status-badge status-unassigned">
                  {{ STATUSES[invoices[0].status] }}
                </span>
              </div>
            </li>
          </ul>
        </div>

        <div v-if="view === 'date-view'" key="date-view" class="adjust-invoice__date">
          <div class="adjust-invoice__body-header">
            <p v-translate>Do you want to change it to today?</p>
          </div>
          <div class="adjust-invoice__number-selector">
            <div class="adjust-invoice__selector-title" v-translate>
              Select a date
            </div>
            <div class="adjust-invoice__options">
              <div
                @click="dateChanged(false)"
                class="adjust-invoice__option adjust-invoice__option--first"
                :class="{ 'adjust-invoice__option--active': !update_date }"
              >
                <span class="main-text">{{ invoice.invoice_date | formatDate }}</span>
                <span v-if="!invoice.paid_up_front" class="sub-text">{{ $gettext('due on') }} {{ invoice.due_date | formatDate }}</span>
                <span v-if="invoice.paid_up_front" class="sub-text">{{ $gettext('Invoice already paid') }}</span>
              </div>

              <div class="adjust-selection-arrow adjust-selection-arrow-left" :class="{ 'adjust-selection-arrow--active': !update_date }">
                <font-awesome-icon icon="fa-solid fa-arrow-left" class="mr-1 ml-2" />
              </div>
              <div  class="adjust-selection-arrow adjust-selection-arrow-right" :class="{ 'adjust-selection-arrow--active': update_date }">
                <font-awesome-icon icon="fa-solid fa-arrow-right" class="mr-2" />
              </div>

              <div 
                v-if="invoices[0].number !== invoices[2].number"
                @click="dateChanged(true)"
                class="adjust-invoice__option adjust-invoice__option--second"
                :class="{ 'adjust-invoice__option--active': update_date }"
              >
                <span class="main-text">{{ today | formatDate }}</span>
                <span v-if="!invoice.paid_up_front" class="sub-text">{{ $gettext('due on') }} {{ due_date | formatDate }}</span>
                <span v-if="invoice.paid_up_front" class="sub-text">{{ $gettext('Invoice already paid') }}</span>
              </div>
            </div>
          </div>

          <ul class="adjust-invoice__item-list gk-table"> 
            <!-- CHOICE -->
            <li 
              class="adjust-invoice__row-item adjust-invoice__row-item--choice"
              :class="{ 'adjust-invoice__row-item--sequenced': isSequenced, 'adjust-invoice__row-item--not-sequenced': !isSequenced }"
            >
              <div class="adjust-invoice__item-data">
                <span v-if="!isSequenced">
                  {{ invoices[1].number }}
                </span>
                <span v-if="isSequenced">
                  {{ invoices[2].number }}
                </span>
              </div>
              <div class="adjust-invoice__item-data adjust-invoice__item-data--fixed-md adjust-invoice__item-data--darker">
                <span>
                  {{ invoices[1].date | formatDate }}
                </span>
              </div>
              <div class="adjust-invoice__item-data adjust-invoice__item-data--fixed-md">
                <span>
                  {{ invoices[1].amount | currency(invoices[1].currency) }}
                </span>
              </div>
              <div class="adjust-invoice__item-data adjust-invoice__item-data--badge">
                <span v-translate class="status-badge status-sending">
                  Sending
                </span>
              </div>
            </li>
            <!-- DRAFT -->
            <li v-if="state.length === 2" class="adjust-invoice__row-item adjust-invoice__row-item--draft">
              <div class="adjust-invoice__item-data">
                <span v-if="!isSequenced">
                  {{ invoices[2].number }}
                </span>
                <span v-if="isSequenced">
                  {{ invoices[1].number }}
                </span>
              </div>
              <div class="adjust-invoice__item-data adjust-invoice__item-data--fixed-md">
                <span>
                  {{ invoices[2].date | formatDate }}
                </span>
              </div>
              <div class="adjust-invoice__item-data adjust-invoice__item-data--fixed-md">
                <span>
                  {{ invoices[2].amount | currency(invoices[2].currency) }}
                </span>
              </div>
              <div class="adjust-invoice__item-data adjust-invoice__item-data--badge">
                <span v-translate class="status-badge status-unassigned">
                  Draft
                </span>
              </div>
            </li>
            <!-- DOTS -->
            <li class="adjust-invoice__row-item adjust-invoice__row-item--transparent">
              <span class="adjust-invoice__row-ellipsis">
                <font-awesome-icon icon="fa-solid fa-circle" />
                <font-awesome-icon icon="fa-solid fa-circle" />
                <font-awesome-icon icon="fa-solid fa-circle" />
              </span>
            </li>
            <!-- REFERENCE -->
            <li class="adjust-invoice__row-item adjust-invoice__row-item--reference">
              <div class="adjust-invoice__item-data">
                <span>
                  {{ invoices[0].number }}
                </span>
              </div>
              <div class="adjust-invoice__item-data adjust-invoice__item-data--fixed-md">
                <span>
                  {{ invoices[0].date | formatDate }}
                </span>
              </div>
              <div class="adjust-invoice__item-data adjust-invoice__item-data--fixed-md">
                <span>
                  {{ invoices[0].amount | currency(invoices[0].currency) }}
                </span>
              </div>
              <div class="adjust-invoice__item-data adjust-invoice__item-data--badge">
                <span class="status-badge status-unassigned">
                  {{ STATUSES[invoices[0].status] }}
                </span>
              </div>
            </li>
          </ul>
        </div>
        <div v-if="view === 'close-editing-view'">
          <p v-translate>This will close the invoice for editing, are you sure?</p>
        </div>
      </div>
    </template>
    <template v-slot:actions>
      <div style="display: flex; gap: 0.5rem; justify-content: flex-end;">
        <GekkoButton 
          v-if="modalButtons.cancel"
          @click="close()"
          :text="$gettext('Cancel')"
          variant="light"
        />
        <GekkoButton 
          v-if="modalButtons.back"
          @click="setView('numbering-view')"
          icon="fa-solid fa-arrow-left"
          :text="$gettext('Back')"
        />
        <GekkoButton 
          v-if="modalButtons.send"
          @click="sendInvoice()"
          icon="fa-solid fa-paper-plane"
          :text="sendText"
        />
        <GekkoButton 
          v-if="modalButtons.next"
          @click="setView('date-view')"
          icon="fa-solid fa-arrow-right"
          icon-right
          :text="$gettext('Next')"
        />
        <GekkoButton 
          v-if="modalButtons.ok"
          @click="clickOk()"
          :text="$gettext('Ok')"
        />
      </div>
    </template>
  </ModalBase>
</template>

<script>
import { mapGetters } from "vuex"

import InvoiceService from "@/store/services/invoice.service.js"

import TitleBar from "@/components/TitleBar"
import GekkoButton from "@/components/GekkoButton"
import ModalBase from "@/components/base_components/ModalBase"

export default {
  name: "InvoiceSendAdjustModal",
  components: {
    ModalBase,
    TitleBar,
    GekkoButton
  },
  props: {
    invoiceId: {
      type: Number,
      default: null
    },
    markAsSent: {
      type: Boolean,
      default: false
    },
  },
  data () {
    return {
      invoice: null,
      invoice_year: null,
      currentInvoiceDate: null,
      due_date: null,
      state: [],
      next_number: null,
      last_number: null,
      isSequenced: null,
      update_date: null,
      today: new Date().toString('yyyy-MM-dd'),
      view: null,
      showModal: false,
      STATUSES: {
        'p': this.$gettext('Paid'),
        's': this.$gettext('Sent')
      }
    }
  },
  computed: {
    ...mapGetters({ 
      currentCompany: 'main/getCurrentCompany'
    }),
    sendText () {
      let send_text = this.$gettext('Send as is')
      if (this.isSequenced || this.update_date) {
        send_text = this.$gettext('Update and send')
      }
      return send_text
    },
    modalButtons () {
      return {
        cancel: this.view === "numbering-view" || this.view === "close-editing-view" || this.state.length === 1,
        ok: this.view === 'close-editing-view',
        next: this.view === "numbering-view" && this.state.length === 2,
        back: this.view === "date-view" && this.state.length === 2,
        send: (this.state.length === 1 || this.view === "date-view") && this.view !== 'close-editing-view',
      }
    }
  },
  created () {
    this.getInvoice()
  },
  mounted () {
  },
  methods: {
    openModal () {
      document.body.classList.add('modal-on')
      this.showModal = true
    },
    close () {
      this.$emit('close')
      document.body.classList.remove('modal-on')
    },
    getInvoice () {
      InvoiceService.retrieve(this.invoiceId).then((response) => {
        this.currentInvoiceDate = 'date1'
        this.invoice = response.data
        this.invoice_year = new Date(this.invoice.invoice_date).getFullYear()
        const query_data = {
          sender_company: this.currentCompany.id,
          year: this.invoice_year,
          invoice: this.invoice.id
        }
        InvoiceService.nextSendNumber(query_data).then((response) => {
          this.invoices = [{ date: '', number: '', status: '', type: '', amount: '' }]
          this.next_number = response.data['next']['number']
          this.last_number = response.data['last']['number']
          this.invoices[0].date = response.data['last']['date']
          this.invoices[0].number = this.last_number
          this.invoices[0].status = this.$gettext('sent')
          this.invoices[0].type = 'previous'
          this.invoices[0].amount = response.data['last']['amount']
          this.invoices[0].currency = response.data['last']['currency']
          this.invoices[0].status = response.data['last']['status']
          this.invoices.push({
            id: this.invoice.id,
            date: this.invoice.invoice_date,
            number: this.invoice.number,
            status: this.invoice.status,
            type: 'current',
            amount: this.invoice.total_amount,
            currency: this.invoice.currency
          })
          this.invoices.push({
            date: response.data['replace']['date'],
            number: response.data['replace']['number'],
            status: this.$gettext('draft'),
            type: 'collateral',
            amount: response.data['replace']['amount'],
            currency: response.data['replace']['currency']
          })
          this.due_date = new Date()
          this.due_date.setDate(this.due_date.getDate() + this.invoice.payment_term)
          this.due_date = this.due_date.toString('yyyy-MM-dd')

          this.checkNeededChanges()

          if (this.markAsSent) {
            this.setView('close-editing-view')
          } else if (this.next_number && (this.next_number !== this.invoice.number)) {
            this.setView('numbering-view')
          } else if (this.invoice.invoice_date !== this.today && this.invoice.status === 'd') {
            this.setView('date-view')
          } else {
            this.sendInvoice()
          }

          if (this.state.length > 0 || this.view === 'close-editing-view') this.openModal()
        })
      })
    },

    setSequenced (val) {
      this.isSequenced = val
    },

    setView (view) {
      this.view = view
      if (this.view === 'numbering-view') {
        window.mixpanel.track('Invoice sequence change: modal numbering view',{})
      }
      if (this.view === 'date-view') {
        window.mixpanel.track('Invoice sequence change: modal date view',{})
      }
    },

    checkNeededChanges () {
      this.state = []
      if (this.next_number && (this.next_number !== this.invoice.number)) {
        this.state.push('number')
      }
      if (this.invoice.invoice_date !== this.today && this.invoice.status === 'd') {
        this.state.push('date')
      }
    },

    sendInvoice () {
      console.log('sendInvoice')
      if (this.state.length > 0) {
        this.updateInvoice()
      } else {
        if (!this.markAsSent) {
          this.$emit('send-invoice', { new_number: this.invoice.number, old_number: this.invoice.number })
        } else {
          window.location.href = window.Urls['invoices_mark_as_sent_invoice'](this.invoice.id)
        }
      }
    },

    dateChanged (update) {
      // Here you could change the current invoice date, not to persist it but to 
      // change it in the visual model so the UI updates.
      // This currentInvoiceDate variable won't be needed as long as
      // the current invoice date changes on selecting one date or the other
      this.currentInvoiceDate = update ? 'date1' : 'date2'
      this.update_date = update

      for (var i = 0; i < 3; i++) {
        if (this.invoices[i].type === 'current') {
          if (update) {
            this.invoices[i].date = this.today
          } else {
            this.invoices[i].date = this.invoice.invoice_date
          }
        }
      }
    },

    updateInvoice () {
      console.log('updateInvoice')
      var data = Object.assign({}, this.invoice)
      if (this.update_date) {
        data.invoice_date = this.today
        data.due_date = this.due_date
        if (this.invoice_payment) {
          data.invoice_payment.payment_date = this.today
        }
      }
      delete data.sender_company
      delete data.number

      if (this.isSequenced) {
        InvoiceService.swapNumbers(this.invoice.id, { target: this.next_number }).then((response) => {
          window.mixpanel.track('Invoice sequence change: numbers swapped', {})
          if (this.update_date) {
            window.mixpanel.track('Invoice sequence change: date changed',{})
            return InvoiceService.partialUpdate(this.invoice.id, data)
          }
          return response
        }).then((response) => {
          if (!this.markAsSent) {
            this.$emit('send-invoice', { new_number: this.next_number, old_number: this.invoice.number })
          } else {
            window.location.href = window.Urls['invoices_mark_as_sent_invoice'](this.invoice.id)
          }
        })
      } else {
        window.mixpanel.track('Invoice sequence change: date changed',{});
        InvoiceService.partialUpdate(this.invoice.id, data).then((response) => {
          if (!this.markAsSent) {
            this.$emit('send-invoice', { new_number: this.invoice.number, old_number: this.invoice.number })
          } else {
            window.location.href = window.Urls['invoices_mark_as_sent_invoice'](this.invoice.id)
          }
        })
      }
    },
    clickOk () {
      if (this.state.indexOf('number') > -1) {
        this.setView('numbering-view')
      } else if (this.state.indexOf('date') > -1) {
        this.setView('date-view')
      } else {
        window.location.href = window.Urls['invoices_mark_as_sent_invoice'](this.invoice.id);
      }
    },

  }
};
</script>

<style lang="scss" scoped>

</style>

<style lang="scss">
.send-invoice-adjust-modal {
  .gk-modal { margin-top: 0; }
  $colorSequenced: #4edcb9;
  $colorNotSequenced: #f6d783;
  .adjust-invoice {
    &__numbering,
    &__date  {
      padding-bottom: 2rem;
    }
    &__body-header {
      margin-bottom: 2rem;
    }
    &__number-selector {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      align-items: center;
    }
    &__selector-title {
      font-size: 22px;
    }
    &__options {
      display: flex;
      align-items: center;
      padding: 1rem 0.75rem 2rem 0.75rem;
    }
    &__option {
      padding: 0.75rem 1rem;
      border-radius: $radiusLg;
      cursor: pointer;
      border: 6px solid white;
      &--first { background: $colorNotSequenced; }
      &--second { background: $colorSequenced; }
      &--active { border-color: #666666; }
      transition: all 0.3s ease;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      .sub-text {
        font-size: 14px;
        color: white;
      }
    }
    .adjust-selection-arrow {
      transition: all 0.3s ease;
      opacity: 0.5;
      &--active {
        opacity: 1;
      }
    }
    &__item-list {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      list-style: none;
      padding: 0;
      margin: 0 auto;
      max-width: 620px;
      font-size: 17px;
    }
    &__row-item {
      display: flex;
      flex: 1;
      align-items: center;
      gap: 1rem;
      padding: 0.25rem 0.5rem;
      border-radius: $radiusMd;
      background: #eeeeee;
      color: #999;

      &--sequenced {
        background: $colorSequenced;
        animation: sequenced 0.7s;
        order: 2;
      }
      &--not-sequenced {
        background: $colorNotSequenced;
        animation: notsequenced 0.7s;
      }
      &--choice {
        
      }
      &--draft {
        
      }
      &--transparent {
        background: transparent;
        display: flex;
        padding: 0;
        .adjust-invoice__row-ellipsis {
          display: flex;
          justify-content: center;
          gap: 0.25rem;
          flex: 1;
          font-size: 6px;
          color: #BBBBBB;
        }
      }
      &--reference {
        order: 3;
      }
    }
    &__item-data {
      &--fixed-md {
        min-width: 110px;
        max-width: 110px;
      }
      &--darker {
        color: #333;
      }
      &--badge {
        margin-left: auto;
      }
    }
  }
}

@keyframes notsequenced {
  0% {
    opacity: 0;
    transform: translateY(-20px);
  }
  50% { 
    opacity: 1;
  }
}

@keyframes sequenced {
  0% {
    opacity: 0;
    transform: translateY(20px);
  }
  50% {
    opacity: 1;
  }
}
</style>
